import { onBeforeUnmount, onMounted, ref } from "vue"
import { router } from "@inertiajs/vue3"
import { Passkeys } from "@laravel/passkeys"

const MAX_AUTOFILL_RETRIES = 3

export default function usePasskeySignIn(sharedDevice: () => boolean) {
    const supported = ref(false)
    const pending = ref(false)

    let mounted = true
    let autofillWanted = false
    let autofillId = 0
    let autofillRetries = 0

    const request = () => ({
        remember: () => !sharedDevice(),
        routes: {
            options: route("passkey.login-options"),
            submit: route("passkey.login"),
        },
    })

    const finish = (redirect?: string) => {
        router.visit(redirect ?? route("dashboard"))
    }

    const rearmAutofill = (id: number) => {
        if (id !== autofillId || !mounted || pending.value || autofillRetries >= MAX_AUTOFILL_RETRIES) {
            return
        }

        autofillRetries++
        armAutofill()
    }

    const armAutofill = () => {
        autofillWanted = true

        if (!supported.value) {
            return
        }

        const id = ++autofillId

        Passkeys.autofill(request())
            .then((response) => {
                if (response) {
                    finish(response.redirect)

                    return
                }

                rearmAutofill(id)
            })
            .catch(() => rearmAutofill(id))
    }

    const signIn = async (): Promise<boolean> => {
        if (!supported.value || pending.value) {
            return false
        }

        pending.value = true
        autofillId++
        Passkeys.cancel()

        try {
            finish((await Passkeys.verify(request())).redirect)

            return true
        } catch {
            pending.value = false

            if (mounted && autofillWanted) {
                armAutofill()
            }

            return false
        }
    }

    onMounted(() => {
        supported.value = Passkeys.isSupported()
    })

    onBeforeUnmount(() => {
        mounted = false
        Passkeys.cancel()
    })

    return { supported, pending, armAutofill, signIn }
}
